import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import classnames from 'classnames'
import type {SVGAttributes} from 'react'
import React from 'react'
import type {OmitStrict} from 'type-zoo'
import type {IHoverTrackingDivProps} from '~/components/UtilityDivs/HoverTrackingDiv'
import HoverTrackingDiv from '~/components/UtilityDivs/HoverTrackingDiv'
import type {IFontAwesomeIconProps} from '~/utils/FontAwesome/types'

interface IHoverableFontAwesomeIconDivProps extends OmitStrict<
  IHoverTrackingDivProps,
  'children'
> {
  icon: IFontAwesomeIconProps
  /** When the div is hovered, these optional icon props will be shallow merged with the original props, if given.
   * Icon props are reused if nothing is specified.
   */
  hoverIcon?: Partial<IFontAwesomeIconProps>
  /** Perform a deep merge of the styles prop above, rather than a shallow replacement. */
  mergeStylesWhenHovered?: boolean
  /** Use both {icon,alternateIcon}.className props, rather than just using alternateIcon.className when present. */
  mergeClassNamesWhenAlternate?: boolean
  children: (icon: React.ReactNode, hovered: boolean) => React.ReactNode
  iconRef?: React.Ref<SVGSVGElement>
}

/**
 * Many of our designs call for an icon to change colors or styles when the div it's inside of is hovered.
 * This div wraps the options for changing an icon given the div's hovered status and returns the correct icon
 * to the children function for placement.
 */
export class HoverableFontAwesomeIconDiv extends React.Component<IHoverableFontAwesomeIconDivProps> {
  render() {
    const {
      icon,
      hoverIcon,
      mergeStylesWhenHovered,
      mergeClassNamesWhenAlternate,
      children,
      iconRef,
      ...divProps
    } = this.props

    return (
      <HoverTrackingDiv {...divProps}>
        {hovered =>
          children(
            <AlternateStateFontAwesomeIcon
              alternateState={hovered}
              icon={icon}
              alternateIcon={hoverIcon}
              mergeStylesWhenAlternate={mergeStylesWhenHovered}
              mergeClassNamesWhenAlternate={mergeClassNamesWhenAlternate}
              ref={iconRef}
            />,
            hovered
          )
        }
      </HoverTrackingDiv>
    )
  }
}

interface IAlternateStateFontAwesomeIconProps extends Pick<
  SVGAttributes<SVGSVGElement>,
  'onClick'
> {
  icon: IFontAwesomeIconProps
  /** When `hovered === true`,
   * these optional icon props will be shallow merged with the original props, if given.
   * Icon props are reused if nothing is specified.
   */
  alternateIcon?: Partial<IFontAwesomeIconProps>
  alternateState: boolean
  /** Perform a deep merge of the {icon,alternateIcon}.styles props, rather than a shallow replacement. */
  mergeStylesWhenAlternate?: boolean
  /** Use both {icon,alternateIcon}.className props, rather than just using alternateIcon.className when present. */
  mergeClassNamesWhenAlternate?: boolean
}

/**
 * Many of our designs call for an icon to change colors or styles due to some user interaction.
 * This class wraps the options for changing an icon given a boolean and returns the correct icon.
 * This is the controlled version, whereas HoverableFontAwesomeIconDiv does hover detection for you.
 *
 * (It's exported to be useful in scenarios where deciding whether or not to show the alternate state of the icon
 * is not trivially decided by a parent div being hovered or not. Hovering is just the most common use case we have.)
 */

export const AlternateStateFontAwesomeIcon = React.forwardRef<
  SVGSVGElement,
  IAlternateStateFontAwesomeIconProps
>((props, ref) => {
  const {
    alternateState,
    icon,
    alternateIcon,
    mergeStylesWhenAlternate,
    mergeClassNamesWhenAlternate,
    ...iconProps
  } = props

  const altStyleRaw: React.CSSProperties | undefined = alternateIcon?.style
  const altStyleToUse: React.CSSProperties | undefined = mergeStylesWhenAlternate
    ? {...icon.style, ...altStyleRaw}
    : altStyleRaw

  const altClassNameRaw: string | undefined = alternateIcon?.className
  const altClassNameToUse: string | undefined = mergeClassNamesWhenAlternate
    ? classnames(icon.className, altClassNameRaw)
    : altClassNameRaw

  const style = (alternateState && altStyleToUse) || icon.style
  const color = (alternateState && alternateIcon?.color) || icon.color
  const faIcon = (alternateState && alternateIcon?.icon) || icon.icon
  const className = (alternateState && altClassNameToUse) || icon.className

  return (
    <FontAwesomeIcon
      ref={ref}
      {...iconProps}
      {...{style, color, icon: faIcon, className}}
    />
  )
})
